import { Checkbox, Skeleton, Tooltip, lucideIcons, tableHelpers } from '@round/creatorbase-ui';
import { getCreatorAccountId } from '../../../helpers';
import { CellContext } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { HeaderContext, getTableMeta } from '../../helpers';
import styles from './SelectCreatorCell.module.css';

const { UserCheck } = lucideIcons;

type Props = CellContext & {
    onToggle: (accountId: number) => void;
};

export const SelectCreatorCell = ({ row: { original }, table, onToggle }: Props) => {
    const { isLoading, platform, selectedAccountIds, isAlreadyInCampaign, areCampaignPostsLoading } =
        getTableMeta(table);

    const accountId = getCreatorAccountId(original, platform);

    if (tableHelpers.isLoadingRow(original) || isLoading || !accountId) {
        return <Skeleton className={styles.skeleton} />;
    }

    return (
        <div className={styles.container}>
            <Checkbox
                checked={selectedAccountIds.has(accountId)}
                onCheckedChange={() => onToggle(accountId)}
                aria-label="Select creator"
            />

            <AlreadyInCampaignIndicator
                isLoading={areCampaignPostsLoading}
                isAlreadyInCampaign={isAlreadyInCampaign(accountId)}
            />
        </div>
    );
};

type SelectAllHeaderProps = HeaderContext & {
    onSelectAll: (accountIds: number[]) => void;
    onClearSelection: () => void;
};

export const SelectAllCreatorsHeader = ({ table, onSelectAll, onClearSelection }: SelectAllHeaderProps) => {
    const { platform, selectedAccountIds } = getTableMeta(table);

    // Loading rows resolve to no account id, so they never count towards the selection.
    const selectableAccountIds = table
        .getRowModel()
        .rows.map(({ original }) => getCreatorAccountId(original, platform))
        .filter((id): id is number => typeof id === 'number');

    const areAllSelected =
        selectableAccountIds.length > 0 && selectableAccountIds.every((id) => selectedAccountIds.has(id));
    const areSomeSelected = selectableAccountIds.some((id) => selectedAccountIds.has(id));

    return (
        <Checkbox
            checked={areAllSelected}
            indeterminate={!areAllSelected && areSomeSelected}
            onCheckedChange={() => (areAllSelected ? onClearSelection() : onSelectAll(selectableAccountIds))}
            disabled={!selectableAccountIds.length}
            aria-label="Select all creators"
        />
    );
};

function AlreadyInCampaignIndicator({
    isLoading,
    isAlreadyInCampaign,
}: {
    isLoading: boolean;
    isAlreadyInCampaign: boolean;
}) {
    if (isLoading) {
        return <Skeleton className={styles.alreadyAddedSkeleton} />;
    }

    if (!isAlreadyInCampaign) {
        return null;
    }

    return (
        <Tooltip delayDuration={100}>
            <Tooltip.Trigger asChild>
                <span aria-label="Already in this campaign">
                    <UserCheck className={styles.alreadyAddedIcon} />
                </span>
            </Tooltip.Trigger>
            <Tooltip.Content side="top">Already in this campaign</Tooltip.Content>
        </Tooltip>
    );
}
